/**
 * HomePage - Rider Home Screen
 * Sprint 28: Displays rider context (name, status, zone, coverage)
 * 
 * Shows:
 * - Rider name and status
 * - Home zone and coverage zones
 * - Quick stats (today's deliveries, earnings)
 * - Status banners for blocked states
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRiderStore } from '@/store/rider';
import { useAuthStore } from '@/store/auth';
import StatusBanner, { getRiderBannerType } from '@/components/StatusBanner';
import StatusToggle from '@/components/rider/StatusToggle';
import { formatCurrency } from '@/lib/utils';
import { 
  MapPin, 
  Truck, 
  DollarSign, 
  Package, 
  ChevronRight,
  User,
  LogOut
} from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  
  const profile = useRiderStore((state) => state.profile);
  const isLoadingProfile = useRiderStore((state) => state.isLoadingProfile);
  const stats = useRiderStore((state) => state.stats);
  const status = useRiderStore((state) => state.status);
  const initializeRider = useRiderStore((state) => state.initializeRider);
  const getBlockReason = useRiderStore((state) => state.getBlockReason);

  // Initialize rider profile on mount
  useEffect(() => {
    initializeRider();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Determine banner type
  const bannerType = getRiderBannerType({
    riderStatus: profile?.accountStatus,
    zoneActive: profile?.homeZone?.isActive,
    hasHomeZone: !!profile?.homeZone,
  });

  const blockReason = getBlockReason();

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-white p-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">
                {isLoadingProfile ? 'Loading...' : (profile?.name || 'Rider')}
              </h1>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <div className={`w-2 h-2 rounded-full ${
                  status === 'available' ? 'bg-green-400 animate-pulse' :
                  status === 'busy' ? 'bg-yellow-400 animate-pulse' :
                  'bg-white/40'
                }`} />
                <span>
                  {status === 'available' ? 'Online' :
                   status === 'busy' ? 'On Delivery' :
                   'Offline'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Sign Out"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Zone Info */}
        <div className="bg-white/10 rounded-xl p-4 mt-4">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium">Home Zone</div>
              <div className="text-white/80 text-sm">
                {isLoadingProfile ? 'Loading...' : 
                 profile?.homeZone?.name || 'Not assigned'}
              </div>
              {profile?.coverageZones && profile.coverageZones.length > 0 && (
                <div className="text-white/60 text-xs mt-1">
                  +{profile.coverageZones.length} coverage zone{profile.coverageZones.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
            {profile?.homeZone?.isActive === false && (
              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-200 text-xs rounded-full">
                Inactive
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 -mt-2">
        {/* Status Banner (if blocked) */}
        {bannerType && (
          <StatusBanner 
            type={bannerType} 
            message={blockReason || undefined}
          />
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="w-4 h-4" />
              <span className="text-xs">Today's Deliveries</span>
            </div>
            <div className="text-2xl font-bold">{stats.todayDeliveries}</div>
          </div>
          <div className="bg-card border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs">Today's Earnings</span>
            </div>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(stats.todayEarnings)}
            </div>
          </div>
        </div>

        {/* Status Toggle */}
        <StatusToggle />

        {/* Quick Actions */}
        <div className="bg-card border rounded-xl divide-y">
          <button
            onClick={() => navigate('/orders')}
            className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <div className="font-medium">Orders</div>
                <div className="text-sm text-muted-foreground">
                  View available and active orders
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate('/earnings')}
            className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <div className="font-medium">Earnings</div>
                <div className="text-sm text-muted-foreground">
                  View your earnings history
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate('/settings')}
            className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-gray-600" />
              </div>
              <div className="text-left">
                <div className="font-medium">Vehicle & Settings</div>
                <div className="text-sm text-muted-foreground">
                  {profile?.vehicleType || 'Motorcycle'} - {profile?.plateNumber || 'Not set'}
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Coverage Zones (if any) */}
        {profile?.coverageZones && profile.coverageZones.length > 0 && (
          <div className="bg-card border rounded-xl p-4">
            <h3 className="font-medium mb-3">Coverage Zones</h3>
            <div className="flex flex-wrap gap-2">
              {profile.coverageZones.map((zone) => (
                <span 
                  key={zone.id}
                  className={`px-3 py-1 rounded-full text-sm ${
                    zone.isActive === false 
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-primary/10 text-primary'
                  }`}
                >
                  {zone.name}
                  {zone.isActive === false && ' (inactive)'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Cooldown Warning */}
        {profile?.inCooldown && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                ⏳
              </div>
              <div>
                <div className="font-medium text-orange-800">In Cooldown</div>
                <div className="text-sm text-orange-700">
                  You are temporarily paused from receiving new assignments. 
                  This will clear automatically.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
